import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { dashboardAPI } from '../../services/api';

const Dashboard = () => {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        photos: 0,
        videos: 0,
    });
    const [hoveredCard, setHoveredCard] = useState(null);

    useEffect(() => {
        fetchDashboardCounts();
    }, []);

    const fetchDashboardCounts = async () => {
        setLoading(true);
        try {
            const response = await dashboardAPI.getCounts();
            if (response.data.success) {
                setStats(response.data.data);
            } else {
                toast.error('Failed to load dashboard data.');
            }
        } catch (error) {
            console.error('Error fetching dashboard counts:', error);
            toast.error('Error loading dashboard data. Please refresh.');
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => num?.toLocaleString() || '0';

    // ----- Inline styles -----
    const mainContentStyle = {
        marginLeft: '260px',       // offset for sidebar (desktop)
        padding: '20px 30px',
        backgroundColor: '#f4f7fc',
        minHeight: '100vh',
    };

    const containerStyle = {
        maxWidth: '1200px',
        margin: '0 auto',
    };

    const headerStyle = {
        marginBottom: '1.5rem',
    };

    const titleStyle = {
        fontSize: '1.5rem',
        fontWeight: 700,
        color: '#0b2b3b',
        margin: '0 0 0.25rem 0',
    };

    const subtitleStyle = {
        fontSize: '0.8rem',
        color: '#64748b',
        margin: 0,
    };

    const cardsContainerStyle = {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1rem',
        marginBottom: '1.5rem',
    };

    const cardStyle = (isHovered) => ({
        flex: '1 0 calc(25% - 1rem)',
        minWidth: '200px',
        background: 'white',
        borderRadius: '12px',
        boxShadow: isHovered
            ? '0 4px 12px rgba(0,0,0,0.08)'
            : '0 1px 3px rgba(0,0,0,0.05)',
        padding: '1rem',
        transition: 'transform 0.2s, box-shadow 0.2s',
        transform: isHovered ? 'translateY(-2px)' : 'none',
        cursor: 'default',
    });

    const cardContentStyle = {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    };

    const cardHeaderStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '0.5rem',
    };

    const labelStyle = {
        fontSize: '0.7rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: '#64748b',
    };

    const iconCircleStyle = (color) => ({
        width: '32px',
        height: '32px',
        backgroundColor: '#eef2ff',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: color || '#2c7da0',
    });

    const valueStyle = {
        fontSize: '1.75rem',
        fontWeight: 700,
        color: '#0b2b3b',
        margin: '0.5rem 0',
        lineHeight: 1.2,
    };

    const linkStyle = {
        fontSize: '0.7rem',
        color: '#2c7da0',
        textDecoration: 'none',
        fontWeight: 500,
        marginTop: 'auto',
    };

    const linkHoverStyle = {
        textDecoration: 'underline',
        color: '#1f5068',
    };

    const spinnerContainerStyle = {
        marginLeft: '260px',
        padding: '20px 30px',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
    };

    // ----- Stat items (only photos & videos) -----
    const statItems = [
        { key: 'photos', label: 'PHOTO GALLERY', icon: 'fa-images', color: '#0ea5e9', link: '/admin/photos' },
        { key: 'videos', label: 'VIDEO GALLERY', icon: 'fa-video', color: '#ef4444', link: '/admin/videos' },
    ];

    const getValue = (key) => {
        switch (key) {
            case 'photos': return stats.photos;
            case 'videos': return stats.videos;
            default: return 0;
        }
    };

    if (loading) {
        return (
            <div style={spinnerContainerStyle}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: '3rem', color: '#2c7da0' }}></i>
                    <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading dashboard data...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={mainContentStyle}>
            <ToastContainer position="top-right" autoClose={3000} />
            <div style={containerStyle}>
                {/* Header */}
                <div style={headerStyle}>
                    <h1 style={titleStyle}>
                        <i className="fas fa-tachometer-alt" style={{ marginRight: '0.5rem' }}></i> Dashboard
                    </h1>
                    <p style={subtitleStyle}>
                        Welcome back! Here's what's happening with your CMS today.
                    </p>
                </div>

                {/* Cards */}
                <div style={cardsContainerStyle}>
                    {statItems.map((item, index) => {
                        const isHovered = hoveredCard === index;
                        return (
                            <div
                                key={item.key}
                                style={cardStyle(isHovered)}
                                onMouseEnter={() => setHoveredCard(index)}
                                onMouseLeave={() => setHoveredCard(null)}
                            >
                                <div style={cardContentStyle}>
                                    <div style={cardHeaderStyle}>
                                        <span style={labelStyle}>{item.label}</span>
                                        <div style={iconCircleStyle(item.color)}>
                                            <i className={`fas ${item.icon}`} style={{ fontSize: '0.9rem', color: item.color }}></i>
                                        </div>
                                    </div>
                                    <div style={valueStyle}>{formatNumber(getValue(item.key))}</div>
                                    <Link
                                        to={item.link}
                                        style={isHovered ? { ...linkStyle, ...linkHoverStyle } : linkStyle}
                                    >
                                        View all →
                                    </Link>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;